import { BCMSCurrency, Branding, Link, useModel, useWorkflowStep } from '@bcm-dev/bcms-vite';
import { getDefaultMultiPay, getWorkflowSteps } from "compid/workflows/MultiPay";
import MultiPayItem from "compid/workflows/MultiPay/classes/MultiPayItem";
import { Account, Bill, Biller, Payment } from "model";
import { useEffect, useState } from "react";

import CustomSelect, { CustomSelectItem } from "compid/custom-ui/CustomSelect";
import LayoutContainer from 'compid/shared/containers/LayoutContainer';
import LeftColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/LeftColumnContainer";
import RightColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/RightColumnContainer";
import BillerInfoView from "compid/shared/views/BillerView/components/views/BillerInfoView";
import BillerPaymentView from "compid/shared/views/BillerView/components/views/BillerPaymentView";
import { DatePickerView } from "compid/ui/DatePicker";
import UIAmountInput from "compid/ui/UIAmountInput";
import UILimitedCharacterInput from "compid/ui/UILimitedCharacterInput";
import { SVGAsset } from 'res';

import './styles.css';


export default function SchedulePaymentWidget(props){
  // Initalize state, setting the default value to the multipay property or a newly create MultiPayItem
  const [ multipay, setMultipay ] = useState(props.multipay ?? MultiPayItem.init(Bill.firstInstanceOf()));
  const workflowStep = useWorkflowStep(getDefaultMultiPay, getWorkflowSteps);
  const { branding } = workflowStep.model;
  const options = workflowStep.model.branding.options;

  // Initalize required variables
  const biller = multipay.bill.biller;
  const paymentDate = multipay.date;
  const paymentType = multipay.bill.biller.paymentType;
  const account = multipay.account;
  const accountNumber = account.number;

  let feeIsSelected = Payment.getIfDateHasFee(paymentDate, biller, workflowStep.model.branding);

  const isPaper = multipay.bill.biller.paymentType === "paper";
  const isElectronic = multipay.bill.biller.paymentType === "electronic";
  const isVirtualCard = multipay.bill.biller.paymentType === "virtualCard";
  const isInstantPayment = multipay.bill.biller.paymentType === "instantPayment";
  const isWithinMinutes = multipay.bill.biller.paymentType === "withinMinutes";

  const getFee = () => {
      if(feeIsSelected) {
        let amount = isPaper
            ? BCMSCurrency.init(options.overnight_check_fee_rxp22 === "undefined" ? "14.95" : options.overnight_check_fee_rxp22).amount
            : BCMSCurrency.init(options.same_day_fee_rxp22 === "undefined" ? "9.95" : options.same_day_fee_rxp22).amount;

        if (isInstantPayment) { amount = BCMSCurrency.init(options.same_day_ip_fee_rxp22 === "Free" ? "0.00" : options.same_day_ip_fee_rxp22).amount; }
        if (isWithinMinutes) { amount = BCMSCurrency.init(options.within_minutes_ip_fee_rxp22 === "Free" ? "0.00" : options.within_minutes_ip_fee_rxp22).amount; }

        return amount;
      }
      
      return 0;
  }

  const dateLabel = isPaper ? "Estimated delivery" : "Deliver by";
  const accountID = multipay.account.id;
  const amount = multipay.amount.format();
  const dueDate = multipay.bill.status.toLowerCase() === "due" ? multipay.bill.date : undefined;
  // onRemove :: If props.onRemove is a function then call that function passing the multipay variable
  const onRemove = () => props.onRemove instanceof Function
    ? props.onRemove(multipay)
    : undefined;
  // onUpdate :: If props.onChange is a function then call that function passing the prop and value parameters
  const onUpdate = (prop, value) => props.onChange instanceof Function
    ? props.onChange(prop, value)
    : undefined;

  const updateValue = prop => value => {
    if(prop === "date") {
      const dateHasFee = Payment.getIfDateHasFee(value, paymentType, branding);

      feeIsSelected = dateHasFee;

      onUpdate("hasFee", dateHasFee);
      onUpdate("feeAmount", dateHasFee ? getFee() : "0");
    }
    
    onUpdate(prop, value);
  };

  const updateAmount = value => onUpdate("amount", value.replace("$", ""));
  const setFeeDate = evt => {
    const date = Payment.getFeeDate(paymentType);
    const dateHasFee = Payment.getIfDateHasFee(date, paymentType, branding);

    feeIsSelected = dateHasFee;

    onUpdate("hasFee", dateHasFee);
    onUpdate("feeAmount", dateHasFee ? getFee() : "0");
    onUpdate("date", date);
  };

  // If the props.multipay changes, update the state
  useEffect(() => setMultipay(props.multipay ?? MultiPayItem.init(Bill.firstInstanceOf())), [ props.multipay ]);

  // START: This biller/last payment messaging was written to match what we see in "Pay Bills".
  const { payments } = useModel();
  const lastPayment = Payment.getLastPaymentByBillerID(multipay.bill.biller.id, payments);

  var description = "",
      billerAmount = "",
      statusLabel = "",
      statusDate = "",
      showPaymentInfo = true;

  if(multipay.bill.status.toLowerCase() !== "due" && lastPayment) {
    description = "Last payment";
    billerAmount = lastPayment.amount;
    statusLabel = lastPayment.status;
    statusDate = lastPayment.formatDate;
  } else if(multipay.bill.status.toLowerCase() === "due") {
    description = "";
    billerAmount = multipay.bill.balance;
    statusLabel = "Due";
    statusDate = multipay.bill.formatDate;
  } else if (multipay.bill.status.toLowerCase() !== "due" && !lastPayment) {
    showPaymentInfo = false;
  }
  // END: This biller/last payment messaging was written to match what we see in "Pay Bills".

  const customSelectItems = Account.initAll().map((account) => (<CustomSelectItem key={ account.id } value={ account.id }>{ account.name + " " + account.number }</CustomSelectItem>));
  const hasRealTimeGoodFunds = Payment.hasRealTimeGoodFunds(paymentType, branding);

  let showFeeLabel = isPaper ? options.overnight_check_rxp22 : options.same_day_payment_rxp22,
      feeLabel = "";

  showFeeLabel = (isInstantPayment || isWithinMinutes) ? options.instant_payments_rxp22 : showFeeLabel;

  if (showFeeLabel === "true") { feeLabel = feeIsSelected ? "Rush delivery selected" : "Rush delivery available"; }
  if ((hasRealTimeGoodFunds && isPaper) || isVirtualCard || isInstantPayment || isWithinMinutes) { feeLabel = ""; }

  const instantPaymentFee = options.same_day_ip_fee_rxp22 === "Free" || options.same_day_ip_fee_rxp22 === "undefined"
      ? "Free"
      : BCMSCurrency.init(options.same_day_ip_fee_rxp22.replace(/[$]/g, "")).format();

  const withinMinutesFee = options.within_minutes_ip_fee_rxp22 === "Free" || options.within_minutes_ip_fee_rxp22 === "undefined"
      ? "Free"
      : BCMSCurrency.init(options.within_minutes_ip_fee_rxp22.replace(/[$]/g, "")).format();

  // START: Create the "Withdrawn on..." message. 
  let withdrawnOnDateLabel = "",
      withdrawnOnDate = Payment.getWithdrawOnDate(paymentDate, paymentType, branding);

  multipay.withdrawnOnDate = withdrawnOnDate;
  withdrawnOnDateLabel = withdrawnOnDate.format("M d");

  let withdrawnLabel = hasRealTimeGoodFunds ? <p data-point={ `Schedule_Payment_Withdrawn_Label_${ multipay.bill.id }` }>Withdrawn on { withdrawnOnDateLabel }</p> : undefined;
  withdrawnLabel = (isInstantPayment || isWithinMinutes) && feeIsSelected ? <p data-point={ `Schedule_Payment_Withdrawn_Label_${ multipay.bill.id }` }>Withdraw immediately</p> : withdrawnLabel;
  // END: Create the "Withdrawn on..." message. 

  //const availableBalanceText = `Available balance: ${ account.balance.format() }`;
  let fundsMessage1 = ``,
      fundsMessage2 = ``,
      hasImmediateWithdrawDate = false;

  if(hasRealTimeGoodFunds) {
      if(withdrawnOnDate.isToday()) {
          fundsMessage1 = `The funds will be withdrawn from your account immediately.`;
          hasImmediateWithdrawDate = true;
  
          if(paymentDate.isToday()) {
              fundsMessage1 = `The funds will be withdrawn from your account ${ accountNumber } immediately`;
              fundsMessage2 = `Once the payment is submitted it cannot be modified or canceled.`;
          }
  
      } else {
          fundsMessage1 = `If funds are not available on ${ withdrawnOnDate.format("M d") }, the payment will fail.`;
      }
  }

  const paperBillerDatePickerSubtext = hasRealTimeGoodFunds
      ? undefined
      : <div className="paper-biller-info-text-container flex-container flex-horizontal align-center">
          <SVGAsset id="ExclamationPointIcon" className="blue-icon left-icon" />
          <p className="sub-text">Your check may be cashed, and the money withdrawn from your account, before, on , or after { multipay.formatDate() }.</p>
        </div>;

  const showInfoTextRight = hasRealTimeGoodFunds && (isElectronic || isInstantPayment || isWithinMinutes);
  const infoTextRight = showInfoTextRight 
      ? <div className="date-helper-text">
            <span className="sub-text payment-type"><i>Electronic</i></span>
        </div>
      : <></>

  return(
    <div className="schedule-payment-widget row-container">
      <div className="flex-container flex-horizontal align-center">
        <BillerInfoView biller={ multipay.bill.biller } />
        { showPaymentInfo && 
        <BillerPaymentView amount={ billerAmount } date={ statusDate } status={ statusLabel } description={ description } />
        }

        { workflowStep.getStepState.items.length > 1 && 
          <span>
            <Link onClick={ onRemove } className="set-width trash-button">
              <SVGAsset id="TrashCanIcon" data-point={ `Schedule_Payment_Delete_Icon_${ multipay.bill.id }` }/>
            </Link>
          </span> }
      </div>

      {/* Gray background area */}
      <div className="form-group">
        <LayoutContainer>
          <LeftColumnContainer>
            <UIAmountInput placeHolder="Amount" value={ amount } onChange={ updateAmount } dataPoint={ `Schedule_Payment_Amount_${ multipay.bill.id }` }/>
            <div className="pay-from-row form-row hide-dropdown-arrow">
              <CustomSelect label="Pay from" helpText={""} value={ accountID } onChange={ updateValue("account") }
              dataPoint={ `Schedule_Payment_Pay_From_${ multipay.bill.id }` }>
                  { customSelectItems }
              </CustomSelect>
            </div>        
          </LeftColumnContainer>
          <RightColumnContainer>
            <div className="form-row date-picker-form-row">

              <DatePickerView setDate={ multipay.date } dateLabel={ dateLabel } rightLabel={ feeLabel }
                onChange={ updateValue("date") } showFees={ "true" } billerType={ multipay.bill.biller.paymentType } 
                dueDate={ dueDate } dataPoint={`Schedule_Payment_${ multipay.bill.id }`} 
                infoTextRight={ infoTextRight }
                infoClasses="flex-width">

                  <div className="flex-container flex-horizontal align-center date-helper-text">
                      <span className="sub-text">{ withdrawnLabel }</span>
                  </div>

                  <Branding id="instant_payments_rxp22" value="true" default="false">
                    <div className="flex-container flex-horizontal align-center date-helper-text">
                        { (isInstantPayment && !feeIsSelected) && <a onClick={ setFeeDate }>Get payment delivered today for { instantPaymentFee }</a> }
                        { (isInstantPayment && feeIsSelected) && <span className="sub-text">Payment will be delivered today</span> }

                        { (isWithinMinutes && !feeIsSelected) && <a onClick={ setFeeDate }>Get payment delivered in minutes for { withinMinutesFee }</a> }
                        { (isWithinMinutes && feeIsSelected) && <span className="sub-text">Payment will be delivered in minutes</span> }
                    </div>
                  </Branding>
              </DatePickerView>
              
            </div>
            { isPaper && <UILimitedCharacterInput placeHolder="Memo" className="memo-input"
            value={ multipay.memo } onChange={ updateValue("memo") } dataPoint={ `Schedule_Payment_Memo_${ multipay.bill.id }` } /> }
          </RightColumnContainer>
        </LayoutContainer>

        { isVirtualCard && 
          <div className="paper-biller-info-text-container flex-container flex-horizontal align-center">
            <p className="sub-text"> { Biller.getVirtualCardDisclaimer() }</p>
          </div> 
        }

        { hasRealTimeGoodFunds &&
            <div className="paper-biller-info-text-container flex-container flex-vertical">
                <div className="flex-container flex-horizontal align-center select-lower-text">
                    <span className="sub-text">{ fundsMessage1 }</span>
                </div>
                <div className="flex-container flex-horizontal align-center select-lower-text">
                    <span className="sub-text">{ fundsMessage2 }</span>
                </div>
            </div>
        }
        
        { isPaper && paperBillerDatePickerSubtext }
      </div>
    </div>
  );
 }